import React, { useState, useEffect, useCallback } from 'react';
import { Pagination } from 'react-bootstrap';
import eventLogService from '../../services/eventLogService';
import { US_STATES } from '../../utils/constants';
import './EventLogSection.css';

const EventLogSection = ({ moduleId, targetUserId, practiceId, sectionFilter, allModules = false }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);
    const [error, setError] = useState(null);

    const fetchLogs = useCallback(async () => {
        if (!allModules && !moduleId) return;

        setLoading(true);
        setError(null);
        try {
            const response = await eventLogService.getLogs(allModules ? null : moduleId, targetUserId ?? null, {
                page,
                limit: 10,
                ...(practiceId != null && { practiceId }),
                ...(sectionFilter && { section: sectionFilter })
            });

            if (response && response.success) {
                setLogs(response.logs ?? []);
                setTotalPages(response.pagination?.total_pages ?? 1);
                setTotalLogs(response.pagination?.total ?? 0);
            } else {
                setLogs([]);
                setTotalPages(1);
                setTotalLogs(0);
            }
        } catch (err) {
            console.error('Failed to fetch logs:', err);
            setError('Unable to load event logs.');
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }, [moduleId, targetUserId, practiceId, page, sectionFilter, allModules]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        }).replace(',', '');
    };

    /**
     * Finds differences between old and new data objects and returns an array of comparison rows.
     */
    /**
     * Finds differences between old and new data objects and returns an array of comparison rows.
     */
    const getComparisonRows = (oldData, newData, moduleType, action) => {
        const actionLower = String(action || '').toLowerCase();
        if (!newData && actionLower !== 'delete') return [];
        const parsedOld = typeof oldData === 'string' ? JSON.parse(oldData || '{}') : (oldData || {});
        const parsedNew = typeof newData === 'string' ? JSON.parse(newData || '{}') : (newData || {});

        // If parsedNew is actually a "changes" object from backend (keys having from/to)
        // we check if it has 'from' and 'to' in its keys.
        const isChangesObject = Object.values(parsedNew).some(val => val && typeof val === 'object' && ('from' in val || 'to' in val));

        const rows = [];

        // Helper to format 24h time string to 12h format
        const formatTime12 = (val) => {
            if (!val || typeof val !== 'string') return val;
            // Match HH:mm or HH:mm:ss
            const timeMatch = val.match(/^(\d{1,2}):(\d{2})(:(\d{2}))?$/);
            if (!timeMatch) return val;
            
            let hours = parseInt(timeMatch[1]);
            const minutes = timeMatch[2];
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12; // 0 becomes 12
            return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
        };

        // Helper to normalize and map values
        const normalize = (key, val) => {
            const boolKeys = ['is_two_way_auth', 'two_way_auth', 'is_foracare', 'is_withings', 'is_default', 'is_online', 'agreement', 'status'];
            
            // Normalize "empty" values to 'N/A'
            if (val === null || val === undefined || String(val).trim() === '' || (typeof val === 'object' && Object.keys(val).length === 0)) {
                return boolKeys.includes(key) ? 'Disabled' : 'N/A';
            }
            
            // Map Title
            if (key === 'title_id' || key === 'title') {
                const titleMap = { 1: 'Dr.', 2: 'Mr.', 3: 'Mrs.', 4: 'Ms.', '1': 'Dr.', '2': 'Mr.', '3': 'Mrs.', '4': 'Ms.' };
                return titleMap[val] || String(val);
            }

            // Map Gender
            if (key === 'gender' || key === 'gender_id') {
                const genderMap = { 1: 'Male', 2: 'Female', 3: 'Other', 0: 'N/A', '1': 'Male', '2': 'Female', '3': 'Other', '0': 'N/A' };
                return genderMap[val] || String(val);
            }

            // Map State IDs to state names
            if (key === 'state_id' || key === 'state_1') {
                const stateObj = US_STATES.find((s) => s.id === parseInt(String(val), 10));
                return stateObj ? stateObj.name : String(val);
            }

            // Map Booleans
            if (boolKeys.includes(key)) {
                if (key === 'status' && typeof val === 'string') {
                    return val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
                }
                const isEnabled = val === 1 || val === '1' || val === true || String(val).toLowerCase() === 'enabled' || String(val).toLowerCase() === 'true';
                return isEnabled ? 'Enabled' : 'Disabled';
            }

            // Time Formatting (if key suggests it's a time field)
            if (key.toLowerCase().includes('time') || key.toLowerCase().includes('period')) {
                return formatTime12(String(val));
            }

            if (typeof val === 'object') return JSON.stringify(val);
            return String(val);
        };

        const skipKeys = [
            'id', 'password', 'created_at', 'updated_at', 'deleted_at', 
            'performer_name', 'role_id', 'practice_id', 'email_verified_at', 'remember_token', 
            'session_time', 'last_login', 'agreement_date', 'ip_address'
        ];

        const scheduleArrayKeys = [
            'dailySchedule', 'bloodPressureTimePeriod', 'bloodGlucoseTimePeriod', 'weightTimePeriod', 
            'bloodPressureTargetRange', 'bloodGlucoseTargetRange', 'weightTargetRange'
        ];

        const processChange = (key, oldVal, newVal) => {
            if (skipKeys.includes(key)) return;

            // Robust Array Diffing for Schedule
            const isScheduleArray = scheduleArrayKeys.includes(key);
            
            if (isScheduleArray || (Array.isArray(oldVal) && Array.isArray(newVal))) {
                const arr1 = Array.isArray(oldVal) ? oldVal : [];
                const arr2 = Array.isArray(newVal) ? newVal : [];
                const length = Math.max(arr1.length, arr2.length);
                
                for (let i = 0; i < length; i++) {
                    const itemOld = arr1[i] || {};
                    const itemNew = arr2[i] || {};
                    const itemName = itemNew.name || itemNew.label || itemOld.name || itemOld.label || `Item ${i+1}`;
                    const allItemFields = new Set([...Object.keys(itemOld), ...Object.keys(itemNew)]);
                    
                        allItemFields.forEach(field => {
                        if (['id', 'name', 'label'].includes(field)) return;
                        const v1 = itemOld[field];
                        const v2 = itemNew[field];
                        
                        // Strict Normalized Comparison
                        const norm1 = normalize(field, v1);
                        const norm2 = normalize(field, v2);

                        if (norm1 !== norm2) {
                            let fieldLabel = field.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
                            fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
                            
                            let sectionName = key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
                            sectionName = sectionName.charAt(0).toUpperCase() + sectionName.slice(1);

                            rows.push({
                                section: sectionName,
                                label: `${itemName} - ${fieldLabel}`,
                                oldVal: norm1,
                                newVal: norm2
                            });
                        }
                    });
                }
                return;
            }


            // Handle Objects (like Weight Target Range if not an array)
            if (typeof oldVal === 'object' || typeof newVal === 'object') {
                if (oldVal !== null || newVal !== null) {
                    const obj1 = (typeof oldVal === 'object' && oldVal !== null && !Array.isArray(oldVal)) ? oldVal : {};
                    const obj2 = (typeof newVal === 'object' && newVal !== null && !Array.isArray(newVal)) ? newVal : {};
                    const subKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
                    
                    if (subKeys.size > 0 && !isScheduleArray) {
                         subKeys.forEach(subKey => {
                            const v1 = obj1[subKey];
                            const v2 = obj2[subKey];
                            
                            const norm1 = normalize(subKey, v1);
                            const norm2 = normalize(subKey, v2);

                            if (norm1 !== norm2) {
                                let label = subKey.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
                                label = label.charAt(0).toUpperCase() + label.slice(1);
                                rows.push({
                                    section: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                                    label: label,
                                    oldVal: norm1,
                                    newVal: norm2
                                });
                            }
                        });
                        return;
                    }
                }
            }

            const v1Norm = normalize(key, oldVal);
            const v2Norm = normalize(key, newVal);

            if (v1Norm !== v2Norm) {
                let label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                if (key === 'phone' || key === 'home_phone' || key === 'phone_number') label = 'Phone Number';
                if (key === 'cell_phone') label = 'Cell Phone';
                if (key === 'is_two_way_auth' || key === 'two_way_auth') label = 'Two Way Auth';
                if (key === 'title_id' || key === 'title') label = 'Title';
                if (key === 'gender_id' || key === 'gender') label = 'Gender';
                
                rows.push({ 
                    section: 'General',
                    label, 
                    oldVal: v1Norm, 
                    newVal: v2Norm 
                });
            }
        };

        if (actionLower === 'delete') {
            const delRows = [];
            const delSnap =
                parsedOld && typeof parsedOld === 'object' && Object.keys(parsedOld).length ? parsedOld : {};
            const inventoryKeys = [
                'id',
                'product_name',
                'model_number',
                'product_category',
                'qty',
                'price',
                'status',
                'received_date',
                'gateway_serial_number'
            ];
            const looksLikeInventoryDelete = ['product_name', 'model_number', 'product_category'].some(
                (k) => delSnap[k] != null && delSnap[k] !== ''
            );
            if (looksLikeInventoryDelete) {
                inventoryKeys.forEach((key) => {
                    if (delSnap[key] === undefined || delSnap[key] === null || delSnap[key] === '') return;
                    delRows.push({
                        section: 'Removed product',
                        label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
                        oldVal: normalize(key, delSnap[key]),
                        newVal: '—'
                    });
                });
            }
            if (parsedNew?.outcome) {
                delRows.push({
                    section: 'Removed product',
                    label: 'Outcome',
                    oldVal: '—',
                    newVal: String(parsedNew.outcome)
                });
            }
            if (!delRows.length && parsedNew && typeof parsedNew === 'object' && looksLikeInventoryDelete) {
                ['product_id', 'product_name', 'model_number', 'product_category'].forEach((key) => {
                    if (parsedNew[key] === undefined || parsedNew[key] === null || parsedNew[key] === '') return;
                    delRows.push({
                        section: 'Removed product',
                        label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
                        oldVal: '—',
                        newVal: normalize(key, parsedNew[key])
                    });
                });
            }

            // Practice / Patient / Provider / Caregiver / Admin / etc.: show snapshot from `data` (oldData)
            const skipDeleteDisplay = new Set([
                'password',
                'remember_token',
                'email_verified_at',
                'session_time',
                'last_login',
                'created_at',
                'updated_at',
                'deleted_at',
                'agreement_date',
                'ip_address',
                'performer_name'
            ]);
            Object.keys(delSnap).forEach((key) => {
                if (inventoryKeys.includes(key)) return;
                if (skipDeleteDisplay.has(key)) return;
                const val = delSnap[key];
                if (val === undefined || val === null || val === '') return;
                if (typeof val === 'object' && !Array.isArray(val)) {
                    if (val instanceof Date) {
                        /* fall through to stringify below */
                    } else if (Object.keys(val).length === 0) {
                        return;
                    }
                }
                let displayVal;
                if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
                    displayVal = JSON.stringify(val);
                } else {
                    displayVal = normalize(key, val);
                }
                delRows.push({
                    section: 'Deleted record',
                    label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
                    oldVal: displayVal,
                    newVal: '—'
                });
            });

            if (!delRows.length && parsedNew?.deleted) {
                delRows.push({
                    section: 'Delete',
                    label: 'Details',
                    oldVal: 'No snapshot was stored for this delete.',
                    newVal: '—'
                });
            }

            return delRows;
        }

        const isCreateAction = String(action || '').toLowerCase() === 'create';
        const createPayload = isCreateAction
            ? (() => {
                // Some create logs store only a subset in update_data; others keep richer data in data.
                // Merge both and prefer non-empty values from update_data.
                const oldObj = (parsedOld && typeof parsedOld === 'object') ? parsedOld : {};
                const newObj = (parsedNew && typeof parsedNew === 'object') ? parsedNew : {};
                const merged = { ...oldObj, ...newObj };
                return merged;
            })()
            : null;

        if (isCreateAction && createPayload) {
            // For create events, show all incoming fields as newly added values.
            Object.keys(createPayload).forEach((key) => {
                const newValue = createPayload[key];
                if (newValue && typeof newValue === 'object' && ('to' in newValue || 'from' in newValue)) {
                    processChange(key, newValue.from, newValue.to);
                } else {
                    processChange(key, undefined, newValue);
                }
            });
        } else if (isChangesObject) {
            // Direct diff from backend
            Object.entries(parsedNew).forEach(([key, change]) => {
                if (change && typeof change === 'object' && ('from' in change || 'to' in change)) {
                    processChange(key, change.from, change.to);
                } else {
                    // Fallback for non-diffed keys
                    processChange(key, parsedOld[key], change);
                }
            });
        } else {
            // Full object comparison
            // For partial updates, only compare keys that were sent in the update request
            const allKeys = (action === 'update' || action === 'Update' || action?.toLowerCase() === 'update')
                ? new Set(Object.keys(parsedNew))
                : new Set([...Object.keys(parsedOld), ...Object.keys(parsedNew)]);

            allKeys.forEach(key => {
                processChange(key, parsedOld[key], parsedNew[key]);
            });
        }

        // Special handling for Person Name if not processed
        const oldName = `${parsedOld.first_name || ''} ${parsedOld.last_name || ''}`.trim();
        const newName = `${parsedNew.first_name || ''} ${parsedNew.last_name || ''}`.trim();
        if (oldName && newName && oldName !== newName && !rows.find(r => r.label.includes('Name'))) {
            rows.unshift({
                label: `${moduleType || 'Entity'} Name`,
                oldVal: oldName,
                newVal: newName
            });
        }

        return rows;
    };

    return (
        <div className="event-log-section-container" id="event-log-section">
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading logs...</span>
                    </div>
                </div>
            ) : error ? (
                <div className="event-log-empty text-danger">{error}</div>
            ) : (
                <fieldset className="log-fieldset">
                    <legend className="log-legend">{allModules ? 'Activity Log' : `${moduleId} Log`}</legend>

                    {logs.length > 0 ? (
                        <div className="log-cards-container">
                            {logs.map((log, index) => {
                                return (
                                    <div key={log.id} className="log-card">
                                        <div className="log-seq-badge">
                                            {(page - 1) * 10 + index + 1}
                                        </div>

                                        <div className="log-meta-grid">
                                            {/* Left Column */}
                                            <div>
                                                <div className="log-meta-item-row mb-3">
                                                    <span className="log-meta-label">User Name</span>
                                                    <span className="log-meta-colon">:</span>
                                                    <span className="log-meta-value">{log.performer_name || 'System'}</span>
                                                </div>
                                                <div className="log-meta-item-row">
                                                    <span className="log-meta-label">Module</span>
                                                    <span className="log-meta-colon">:</span>
                                                    <span className="log-meta-value">{log.module || moduleId}</span>
                                                </div>
                                            </div>

                                            {/* Right Column */}
                                            <div>
                                                <div className="log-meta-item-row mb-3">
                                                    <span className="log-meta-label">Action</span>
                                                    <span className="log-meta-colon">:</span>
                                                    <span className="log-meta-value text-capitalize">{(log.action || '').replace(/_/g, ' ')}</span>
                                                </div>
                                                <div className="log-meta-item-row">
                                                    <span className="log-meta-label">Date</span>
                                                    <span className="log-meta-colon">:</span>
                                                    <span className="log-meta-value">{formatDate(log.created_at)}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {(() => {
                                            const actionLower = String(log.action || '').toLowerCase();
                                            if (actionLower === 'delete') {
                                                const deleteRows = getComparisonRows(
                                                    log.data,
                                                    log.update_data,
                                                    log.module || moduleId,
                                                    log.action
                                                );
                                                const deleteSections = [...new Set(deleteRows.map((r) => r.section))];

                                                return (
                                                    <div className="log-delete-block">
                                                        {deleteRows.length > 0 ? (
                                                            <div className="log-data-box log-delete-snapshot-box">
                                                                <div className="log-data-header log-delete-snapshot-header">
                                                                    Record deleted
                                                                </div>
                                                                <div className="log-data-content">
                                                                    {deleteSections.map((section) => (
                                                                        <div key={section} className="log-change-section">
                                                                            <div className="log-section-title">{section}</div>
                                                                            <table className="log-data-table">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th className="log-data-key-cell log-delete-th">Field</th>
                                                                                        <th className="log-data-val-cell log-delete-th">Value</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {deleteRows
                                                                                        .filter((r) => r.section === section)
                                                                                        .map((row, idx) => (
                                                                                            <tr key={idx}>
                                                                                                <td className="log-data-key-cell">{row.label}</td>
                                                                                                <td className="log-data-val-cell">{row.oldVal}</td>
                                                                                            </tr>
                                                                                        ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="log-delete-no-snapshot">
                                                                No field snapshot was stored for this delete.
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            }

                                            const comparisonRows = getComparisonRows(log.data, log.update_data, log.module || moduleId, log.action);

                                            if (comparisonRows.length === 0) {
                                                return (
                                                    <div className="log-no-changes-alert">
                                                        No specific field changes recorded for this event.
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div className="log-data-comparison-container">
                                                    {/* Old Data Box */}
                                                    <div className="log-data-box">
                                                        <div className="log-data-header">Old Data</div>
                                                        <div className="log-data-content">
                                                            {(() => {
                                                                const sections = [...new Set(comparisonRows.map(r => r.section))];
                                                                return sections.map(section => (
                                                                    <div key={section} className="log-change-section">
                                                                        <div className="log-section-title">{section}</div>
                                                                        <table className="log-data-table">
                                                                            <tbody>
                                                                                {comparisonRows.filter(r => r.section === section).map((row, idx) => (
                                                                                    <tr key={idx}>
                                                                                        <td className="log-data-key-cell">{row.label}</td>
                                                                                        <td className="log-data-val-cell">{row.oldVal}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                ));
                                                            })()}
                                                        </div>
                                                    </div>

                                                    {/* New Data Box */}
                                                    <div className="log-data-box">
                                                        <div className="log-data-header">New Data</div>
                                                        <div className="log-data-content">
                                                            {(() => {
                                                                const sections = [...new Set(comparisonRows.map(r => r.section))];
                                                                return sections.map(section => (
                                                                    <div key={section} className="log-change-section">
                                                                        <div className="log-section-title">{section}</div>
                                                                        <table className="log-data-table">
                                                                            <tbody>
                                                                                {comparisonRows.filter(r => r.section === section).map((row, idx) => (
                                                                                    <tr key={idx}>
                                                                                        <td className="log-data-key-cell">{row.label}</td>
                                                                                        <td className="log-data-val-cell log-new-val-highlight">{row.newVal}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                ));
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="event-log-empty">
                            <i className="fas fa-file-alt fa-3x mb-3 text-muted"></i>
                            <p>
                                {allModules
                                    ? 'No event logs found yet. Changes recorded across practices and modules will appear here.'
                                    : `No logs found for this ${moduleId}. Modifying data will generate logs here.`}
                            </p>
                        </div>
                    )}

                    {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4 border-top pt-4">
                            <div className="pagination-info">Total Events: {totalLogs}</div>
                            <div className="event-log-pagination-right">
                                <div className="event-log-page-selector">
                                    <span>Page</span>
                                    <select
                                        className="event-log-page-select"
                                        value={page}
                                        onChange={(e) => handlePageChange(parseInt(e.target.value))}
                                    >
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                            <option key={p} value={p}>{p}</option>
                                        ))}
                                    </select>
                                    <span>of {totalPages}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </fieldset>
            )}
        </div>
    );
};

export default EventLogSection;
